/* eslint-disable zeffy-linter-frontend/no-classname-prop -- legacy baseline (pre-rule); remove when these are migrated */
import { useCallback, useEffect } from "react";

import { luxonValidTimezones, getTimezoneStrFromTimezone, Undefinable } from "@simplyk/common";
import { FieldValues, useController, useWatch } from "react-hook-form";

import { TimezonePickerProps } from "./types";

import { useFormInputRefs } from "@/common/hooks/useFormInputRefs";
import { useValidators } from "@/common/hooks/useValidators";
import { DashboardAutocomplete } from "@/dashboard-design-system/Autocomplete/DashboardAutocomplete";
import { AutocompleteOption } from "@/dashboard-design-system/Autocomplete/types";

export function DashboardTimezonePicker<T extends FieldValues>({
  control,
  className,
  defaultTimezone,
  disabled,
  error,
  inputRef,
  name,
  rules,
  size,
}: TimezonePickerProps<T>) {
  const selectedTimezone = useWatch({ control, name }) as string;
  const currentDefaultTimezone = selectedTimezone || defaultTimezone;

  const validators = useValidators(rules);
  const { field } = useController<T>({
    name,
    control,
    defaultValue: currentDefaultTimezone as never,
    rules: validators,
  });

  useEffect(() => {
    // Workaround to make sure the default value is set when the component is mounted
    field.onChange(field.value || currentDefaultTimezone);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onChange = useCallback(
    (value: Undefinable<AutocompleteOption<unknown>> | string) => {
      if (typeof value === "string") {
        return;
      }
      field.onChange(value?.value);
    },
    [field]
  );

  const refCallBack = useFormInputRefs({ inputRefCallBack: field.ref, inputRef });

  return (
    <DashboardAutocomplete
      inputRef={refCallBack}
      disableClearable
      disabled={disabled}
      error={error}
      onChange={onChange}
      options={luxonValidTimezones.map((timezone) => ({ label: timezone.timezoneStr, value: timezone.timezone }))}
      value={{ label: getTimezoneStrFromTimezone(selectedTimezone), value: field.value }}
      size={size}
      className={className}
    />
  );
}
